<template>
  <tbody :class="tbodyClass">
    <ui-expand-header
      :headers="headers"
      :inner-sort="innerSort"
      :is-open="isOpen"
      :is-sortable="isSortable"
      :has-expand-panel="hasExpandPanel"
      :is-expand-arrow-shown="!!isExpandArrowShown"
      :item-class="itemClass"
      :group="group"
      :selectable="selectable"
      :selected="selected"
      @select="$emit('select', group)"
      @onOpen="onOpenGroup($event, group)"
      @onChangeSort="onChangeSort"
    >
      <template v-for="header in headers" v-slot:[`header.${header.value}`]="{ item }">
        <slot
          v-if="$scopedSlots[`header.${header.value}`]"
          :name="`header.${header.value}`"
          :item="item"
          :value="item[header.value]"
          :header="header"
        >
          <span v-if="header.cropLength" :key="`crop-h-${header.value}`" v-tooltip="isCropped(item[header.value], header.cropLength) ? item[header.value] : undefined">
            {{ item[header.value] | cropString(header.cropLength) }}
          </span>
          <span v-else :key="`plain-h-${header.value}`">{{ item[header.value] }}</span>
        </slot>

        <slot v-else :name="`item.${header.value}`" :item="item" :value="getValue(item, header.value)">
          <span v-if="header.cropLength" :key="`crop-${header.value}`" v-tooltip="isCropped(getValue(item, header.value), header.cropLength) ? getValue(item, header.value) : undefined">
            {{ getValue(item, header.value) | cropString(header.cropLength) }}
          </span>
          <span v-else :key="`plain-${header.value}`">{{ getValue(item, header.value) }}</span>
        </slot>
      </template>
    </ui-expand-header>
    <template v-if="isOpen">
      <tr v-if="innerHeaders.length" class="inner-table-headers">
        <td v-if="selectable" />
        <td
          v-for="(header, index) in filteredHeaders"
          :key="index"
          :colspan="header.colspan || 1"
          :class="header.class"
          :style="{
            ...(header.bgcolor ? { backgroundColor: header.bgcolor } : {}),
            '--inner-border-color': header.borderColor || 'var(--v-black-lighten6)',
            ...(header.width ? { width: header.width, minWidth: header.width, maxWidth: header.width } : {}),
          }"
        >
          <slot :name="`inner-header.${header.value}`" :group="group" :header="header">
            {{ header.text }}
          </slot>
        </td>
      </tr>
      <template v-for="subItem in sortedSubItems(items, 2)">
        <ui-expand-row
          :key="subItem.id"
          :headers="secondRowHeaders"
          :inner-sort="innerSort"
          :is-open="openedSecondGroupsId.includes(subItem.id)"
          :is-expand-arrow-shown="!!subItem[thirdGroupKey]?.length"
          :item-class="itemClass"
          :selectable="selectable"
          :gr-ca-report="grCaReport"
          :group="subItem"
          multi-group-second-level
          @onOpen="onOpenSubGroup($event, subItem)"
        >
          <template v-for="subheader in secondRowHeaders" v-slot:[`subheader.${subheader.value}`]="{ item }">
            <slot
              v-if="$scopedSlots[`subheader.${subheader.value}`]"
              :name="`subheader.${subheader.value}`"
              :item="item"
              :parent="group"
              :value="item[subheader.value]"
              :header="subheader"
            >
              {{ item[subheader.value] }}
            </slot>

            <slot v-else :name="`item.${subheader.value}`" :item="item" :value="getValue(item, subheader.value)">
              <span v-if="subheader.cropLength" :key="`crop-${subheader.value}`" v-tooltip="isCropped(getValue(item, subheader.value), subheader.cropLength) ? getValue(item, subheader.value) : undefined">
                {{ getValue(item, subheader.value) | cropString(subheader.cropLength) }}
              </span>
              <span v-else :key="`plain-${subheader.value}`">{{ getValue(item, subheader.value) }}</span>
            </slot>
          </template>
        </ui-expand-row>
        <template v-if="openedSecondGroupsId.includes(subItem.id)">
          <template v-for="content in sortedSubItems(subItem[thirdGroupKey], 3)">
            <ui-expand-row
              :key="content.id"
              :headers="headers"
              :inner-sort="innerSort"
              :is-open="openedThirdGroupsId.includes(content.id)"
              :is-expand-arrow-shown="!!content[fourthGroupKey]?.length"
              :item-class="itemClass"
              :selectable="selectable"
              :gr-ca-report="grCaReport"
              :group="content"
              multi-group-third-level
              @onOpen="onOpenThirdGroup($event, content)"
            >
              <template v-for="subheader in headers" v-slot:[`subheader.${subheader.value}`]="{ item }">
                <slot
                  v-if="$scopedSlots[`thirdRow.${subheader.value}`]"
                  :name="`thirdRow.${subheader.value}`"
                  :item="item"
                  :parent="subItem"
                  :value="item[subheader.value]"
                  :header="subheader"
                >
                  {{ item[subheader.value] }}
                </slot>
                <slot v-else :name="`item.${subheader.value}`" :item="item" :value="getValue(item, subheader.value)">
                  {{ getValue(item, subheader.value) }}
                </slot>
              </template>
            </ui-expand-row>
            <template v-if="openedThirdGroupsId.includes(content.id)">
              <template v-for="subContent in sortedSubItems(content[fourthGroupKey], 4)">
                <ui-expand-row
                  :key="subContent.id"
                  :headers="headers"
                  :inner-sort="innerSort"
                  :is-open="openedFourthGroupsId.includes(subContent.id)"
                  :is-expand-arrow-shown="!!subContent[fifthGroupKey]?.length"
                  :item-class="itemClass"
                  :selectable="selectable"
                  :gr-ca-report="grCaReport"
                  :group="subContent"
                  multi-group-fourth-level
                  @onOpen="onOpenFourthGroup($event, subContent)"
                >
                  <template v-for="subheader in headers" v-slot:[`subheader.${subheader.value}`]="{ item }">
                    <slot
                      v-if="$scopedSlots[`fourthRow.${subheader.value}`]"
                      :name="`fourthRow.${subheader.value}`"
                      :item="item"
                      :parent="content"
                      :value="item[subheader.value]"
                      :header="subheader"
                    >
                      {{ item[subheader.value] }}
                    </slot>
                    <slot v-else :name="`item.${subheader.value}`" :item="item" :value="getValue(item, subheader.value)">
                      {{ getValue(item, subheader.value) }}
                    </slot>
                  </template>
                </ui-expand-row>
                <template v-if="openedFourthGroupsId.includes(subContent.id)">
                  <ui-expand-row
                    v-for="ffgContent in sortedSubItems(subContent[fifthGroupKey], 5)"
                    :key="ffgContent.id"
                    :headers="filteredHeaders"
                    :inner-sort="innerSort"
                    :item-class="itemClass"
                    :selectable="selectable"
                    :gr-ca-report="grCaReport"
                    :group="ffgContent"
                    multi-group-fifth-level
                    :is-expand-arrow-shown="false"
                    @onChangeSort="onChangeSort"
                  >
                    <template v-for="subheader in headers" v-slot:[`subheader.${subheader.value}`]="{ item }">
                      <slot
                        v-if="$scopedSlots[`fifthRow.${subheader.value}`]"
                        :name="`fifthRow.${subheader.value}`"
                        :item="item"
                        :parent="subContent"
                        :value="item[subheader.value]"
                        :header="subheader"
                      >
                        {{ item[subheader.value] }}
                      </slot>
                      <slot v-else :name="`item.${subheader.value}`" :item="item" :value="getValue(item, subheader.value)">
                        {{ getValue(item, subheader.value) }}
                      </slot>
                    </template>
                  </ui-expand-row>
                </template>
              </template>
            </template>
          </template>
        </template>
      </template>
    </template>
    <slot name="append" />
  </tbody>
</template>

<script>
import UiExpandHeader from '@/components/ui/framework/general/UIExpandGroup/UIExpandHeader.vue'
import UiExpandRow from '@/components/ui/framework/general/UIExpandGroup/UIExpandRow.vue'
import get from 'lodash/get'
import isString from 'lodash/isString'
import orderBy from 'lodash/orderBy'
import { randomString, splitArr } from '@/helpers/functions'
import { alphabet } from '@/constants/alphabet'
export default {
  name: 'UiExpandGroup',
  components: {
    UiExpandRow,
    UiExpandHeader,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    group: {
      type: Object,
      default: () => ({}),
    },
    openedGroups: {
      type: Array,
      default: () => [],
    },
    openedSubGroups: {
      type: Array,
      default: () => [],
    },
    openedThirdGroups: {
      type: Array,
      default: () => [],
    },
    openedFourthGroups: {
      type: Array,
      default: () => [],
    },
    headers: {
      type: Array,
      default: () => [],
    },
    innerHeaders: {
      type: Array,
      default: () => [],
    },
    options: {
      type: Object,
      default: () => ({}),
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    fixedColsCount: {
      type: Number,
      default: () => 0,
    },
    hasFixedCols: {
      type: Boolean,
      default: () => false,
    },
    innerSort: {
      type: Boolean,
      default: false,
    },
    valueMutator: {
      type: Function,
      default: () => null,
    },
    columnsToShow: {
      type: Array,
      default: () => [],
    },
    closeOnDataChange: {
      type: Boolean,
      default: false,
    },
    grCaReport: {
      type: Boolean,
      default: false,
    },
    disableSort: {
      type: Boolean,
      default: false,
    },
    hideExpandArrow: {
      type: Boolean,
      default: false,
    },
    hasExpandPanel: {
      type: Boolean,
      default: false,
    },
    secondGroupKey: {
      type: String,
      default: 'sg',
    },
    thirdGroupKey: {
      type: String,
      default: 'tg',
    },
    fourthGroupKey: {
      type: String,
      default: 'fg',
    },
    fifthGroupKey: {
      type: String,
      default: 'ffg',
    },
    itemClass: {
      type: Function,
      default: () => '',
    },
    // checkbox cell before the group row (ui-date-table show-select with overridden body), emits select
    selectable: {
      type: Boolean,
      default: false,
    },
    selected: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isOpen: false,
      isSortable: false,
      filteredHeaders: [],
      currentIterItem: null,
      openedItemGroups: [],
      openedSecondGroupsId: [],
      openedThirdGroupsId: [],
      openedFourthGroupsId: [],
    }
  },
  computed: {
    isExpandArrowShown() {
      return !this.hideExpandArrow && this.items.length
    },
    innerSortKey() {
      return this.options?.sortBy[0] || this.headers[0].value
    },
    tbodyClass() {
      return randomString(alphabet, 8).join('')
    },
    secondRowHeaders() {
      return this.innerHeaders.length ? this.filteredHeaders : this.headers
    },
  },
  watch: {
    isOpen: {
      handler(val) {
        if (val && this.hasFixedCols) {
          setTimeout(() => {
            this.fixCols('.fixed-col.right.expanded-col')
          }, 0)
        }
      },
    },
    items: {
      handler() {
        if (this.closeOnDataChange) {
          this.isOpen = false
        }
      },
    },
    columnsToShow(data) {
      this.setHeaders(data)
      this.detachCols()
      this.$nextTick(() => {
        this.fixCols()
      })
    },
    openedGroups: {
      handler(newVal) {
        this.isOpen = newVal.includes(this.group.id)
      },
      immediate: true,
    },
    openedSubGroups: {
      handler(newVal) {
        this.openedSecondGroupsId = [...newVal]
      },
      immediate: true,
    },
    openedThirdGroups: {
      handler(newVal) {
        this.openedThirdGroupsId = [...newVal]
      },
      immediate: true,
    },
    openedFourthGroups: {
      handler(newVal) {
        this.openedFourthGroupsId = [...newVal]
      },
      immediate: true,
    },
  },
  created() {
    this.setHeaders(this.columnsToShow)
  },
  mounted() {
    if (this.hasFixedCols) {
      this.fixCols()
    }
  },
  methods: {
    isCropped(value, length) {
      return typeof value === 'string' && value.length > length
    },
    sortedSubItems(arr, level) {
      const monthOrder = [
        'january',
        'february',
        'march',
        'april',
        'may',
        'june',
        'july',
        'august',
        'september',
        'october',
        'november',
        'december',
      ]
      const getMonthIndex = month => monthOrder.indexOf(month.toLowerCase())
      const getWeekStart = weekRange => this.$moment(weekRange.split(' - ')[0], 'DD.MM.YYYY')
      const getCurrentLevelSort = () =>
        ['sortBy' + level] in this.filters && this.filters?.['sortBy' + level].length ? this.filters?.['sortBy' + level][0] : this.innerSortKey

      function sortGroupList(group, sortKey) {
        const value = get(group, sortKey)
        if (sortKey.includes('month')) {
          group.g.monthIndex = value && isString(value) ? getMonthIndex(value) : -1
          return group
        } else if (sortKey.includes('week')) {
          return value && isString(value) ? getWeekStart(value) : value
        } else {
          return value && isString(value) ? value.toLowerCase() : value
        }
      }

      const sortKey = getCurrentLevelSort() === 'g.month' ? 'g.month_title' : getCurrentLevelSort()
      const orders =
        ((['sortDesc' + level] in this.filters && this.filters?.['sortDesc' + level].length ? this.filters?.['sortDesc' + level][0] : this.options?.sortDesc[0]) ||
          this.isSortable)
          ? 'desc'
          : 'asc'
      const sortFunc = group => sortGroupList(group, sortKey)
      if (this.disableSort) return arr
      if (sortKey === 'g.month_title') {
        orderBy(arr, sortFunc, orders)
        return orderBy(arr, [i => i.g.year, i => i.g.monthIndex], [orders, orders])
      } else {
        return orderBy(arr, sortFunc, orders)
      }
    },
    fixCols(classes = '.fixed-col.right') {
      const rows = splitArr(document.querySelectorAll(`.${this.tbodyClass} ${classes}`), this.fixedColsCount, true)
      rows.forEach(row => {
        let prevElWidth = 0
        row.forEach((col, index) => {
          const rect = col.getBoundingClientRect()
          prevElWidth += rect.width

          if (index === 0) {
            col.style.right = '0px'
            return
          }

          let offset = rect.width
          let offsetNext = 0

          if (index > 0) offsetNext = Math.abs(offset - prevElWidth)
          col.style.right = offsetNext + 'px'
        })
      })
    },
    detachCols() {
      document.querySelectorAll(`.${this.tbodyClass} .fixed-col.right`).forEach(item => {
        item.style.right = null
      })
    },
    getValue(item, key) {
      const value = get(item, key)
      if (this.valueMutator) {
        return this.valueMutator({
          value,
          item,
          key,
        })
      }
      return value
    },
    onOpenGroup(isOpen, item) {
      this.isOpen = isOpen
      if (!isOpen && item.groups?.length) {
        item.groups?.forEach(group => {
          this.openedSecondGroupsId = this.openedSecondGroupsId.filter(el => el !== group.id)
        })
      }
      if (!isOpen) {
        this.openedFourthGroupsId = []
      }
      this.$emit('openedGroup', item.id, isOpen, item, this.items)
    },
    onOpenSubGroup(isOpen, item) {
      if (isOpen) this.openedSecondGroupsId.push(item.id)
      if (!isOpen) this.openedSecondGroupsId = this.openedSecondGroupsId.filter(el => el !== item.id)
      this.$emit('openedSubGroup', item.id, isOpen, item)
    },
    onOpenThirdGroup(isOpen, item) {
      if (isOpen) this.openedThirdGroupsId.push(item.id)
      if (!isOpen) this.openedThirdGroupsId = this.openedThirdGroupsId.filter(el => el !== item.id)
      this.$emit('openedThirdGroup', item.id, isOpen, item)
    },
    onOpenFourthGroup(isOpen, item) {
      if (isOpen) this.openedFourthGroupsId.push(item.id)
      if (!isOpen) this.openedFourthGroupsId = this.openedFourthGroupsId.filter(el => el !== item.id)
      this.$emit('openedFourthGroup', item.id, isOpen, item)
    },
    onChangeSort(isSortable) {
      this.isSortable = isSortable
    },
    setHeaders(selectedHeaders = []) {
      let currentHeaders = this.innerHeaders.length ? this.innerHeaders : this.headers
      if (!selectedHeaders.length) {
        this.filteredHeaders = [...currentHeaders]
        return
      }
      const colIDs = selectedHeaders.map(selectedItem => selectedItem.value)
      this.filteredHeaders = currentHeaders.filter(item => {
        return !colIDs.includes(item.value)
      })
    },
  },
}
</script>

<style>
tr.inner-table-headers td {
  color: #aeaeae !important;
  font-weight: 400 !important;
  font-size: 12px !important;
  line-height: 20px !important;
}
.ui-data-table.v-data-table tbody > tr.inner-table-headers > td:not(.v-data-table__mobile-row) {
  border-bottom: 1px solid var(--inner-border-color) !important;
}
</style>
